/**
 * Set Password Page
 *
 * Page for setting password during account activation or password reset.
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { Eye, EyeOff, Check, X, AlertCircle } from 'lucide-react';
import { setPasswordSchema, passwordRules, TokenType } from '@bstock/shared';
import type { SetPasswordInput, ValidateTokenResponse } from '@bstock/shared';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, Loader } from '@/components/ui';
import { authService } from '@/api/services/auth.service';
import bstockLogo from '@/assets/bstock-logo.svg';

// Password validation rules display
interface PasswordRule {
  label: string;
  test: (value: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { label: `${passwordRules.minLength} caracteres minimum`, test: (v) => v.length >= passwordRules.minLength },
  { label: 'Une majuscule', test: (v) => /[A-Z]/.test(v) },
  { label: 'Une minuscule', test: (v) => /[a-z]/.test(v) },
  { label: 'Un chiffre', test: (v) => /[0-9]/.test(v) },
];

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [tokenInfo, setTokenInfo] = useState<ValidateTokenResponse | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SetPasswordInput & { confirmPassword: string }>({
    resolver: zodResolver(
      setPasswordSchema.extend({
        confirmPassword: setPasswordSchema.shape.password,
      }).refine((data) => data.password === data.confirmPassword, {
        message: 'Les mots de passe ne correspondent pas',
        path: ['confirmPassword'],
      })
    ),
    defaultValues: {
      token: token || '',
      password: '',
      confirmPassword: '',
    },
  });

  const passwordValue = watch('password') || '';
  const confirmValue = watch('confirmPassword') || '';

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError("Lien invalide - token manquant");
        setIsValidating(false);
        return;
      }

      try {
        const info = await authService.validateToken(token);
        setTokenInfo(info);
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { error?: { message?: string } } } };
        setTokenError(axiosError.response?.data?.error?.message || "Ce lien n'est plus valide");
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Check if password meets all rules
  const rulesStatus = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, passed: rule.test(passwordValue) })),
    [passwordValue]
  );

  const allRulesPassed = rulesStatus.every((r) => r.passed);
  const passwordsMatch = passwordValue === confirmValue && confirmValue.length > 0;

  const onSubmit = async (data: SetPasswordInput & { confirmPassword: string }) => {
    setSubmitError(null);
    try {
      const result = await authService.setPassword(data.token, data.password);

      // Store tokens
      localStorage.setItem('accessToken', result.tokens.accessToken);
      localStorage.setItem('refreshToken', result.tokens.refreshToken);

      // Redirect to dashboard
      navigate('/', { replace: true });
      // Force page reload to re-initialize AuthContext
      window.location.reload();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: { message?: string } } } };
      setSubmitError(axiosError.response?.data?.error?.message || "Erreur lors de l'activation");
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader />
      </div>
    );
  }

  // Token error state
  if (tokenError || !tokenInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <img src={bstockLogo} alt="BStock" className="h-12" />
          </div>

          <Card>
            <CardContent className="pt-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-dark-500 mb-2">
                Lien invalide ou expire
              </h2>
              <p className="text-sm text-neutral-600 mb-6">
                {tokenError || "Ce lien d'activation n'est plus valide. Il a peut-etre deja ete utilise ou a expire."}
              </p>
              <p className="text-sm text-neutral-500 mb-6">
                Si vous avez besoin d'un nouveau lien, contactez votre administrateur.
              </p>
              <Link to="/login">
                <Button variant="primary" className="w-full">
                  Retour a la connexion
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main form
  const isActivation = tokenInfo.type === TokenType.ACTIVATION;
  const title = isActivation ? 'Activez votre compte' : 'Reinitialiser votre mot de passe';
  const buttonText = isActivation ? 'Activer mon compte' : 'Reinitialiser';

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={bstockLogo} alt="BStock" className="h-12" />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl">{title}</CardTitle>
            <p className="text-center text-sm text-neutral-500 mt-2">
              Bienvenue {tokenInfo.firstName} !
              <br />
              <span className="text-neutral-400">({tokenInfo.email})</span>
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {submitError && (
                <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                  {submitError}
                </div>
              )}

              <input type="hidden" {...register('token')} />

              {/* Password field */}
              <div>
                <label className="block text-sm font-medium text-dark-500 mb-1">
                  Mot de passe
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    placeholder="Votre mot de passe"
                    error={errors.password?.message}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Password rules */}
              <div className="space-y-1.5">
                {rulesStatus.map((rule, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 text-sm ${
                      rule.passed ? 'text-green-600' : 'text-neutral-400'
                    }`}
                  >
                    {rule.passed ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                    <span>{rule.label}</span>
                  </div>
                ))}
              </div>

              {/* Confirm password field */}
              <div>
                <label className="block text-sm font-medium text-dark-500 mb-1">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <Input
                    type={showConfirm ? 'text' : 'password'}
                    {...register('confirmPassword')}
                    placeholder="Confirmez votre mot de passe"
                    error={errors.confirmPassword?.message}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                isLoading={isSubmitting}
                disabled={!allRulesPassed || !passwordsMatch}
              >
                {buttonText}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
